import { useState } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { CONTACT } from "@/constants/site";
import SEO, { breadcrumb } from "@/components/SEO";

const WHATSAPP_NUMBER = "918390860276";

function isMobile() {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(navigator.userAgent);
}

function buildWhatsAppMessage(f) {
  return (
    "New Enquiry – AM Group Of Education\n\n" +
    `Name: ${f.name}\n` +
    `Phone: ${f.phone}\n` +
    `Email: ${f.email}\n` +
    `Subject: ${f.subject || "-"}\n` +
    `Course Interested In: ${f.course || "-"}\n` +
    `Message: ${f.message}`
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", course: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    // Required fields
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    // Basic email + phone sanity checks
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setLoading(true);

    const text = buildWhatsAppMessage(form);
    const encoded = encodeURIComponent(text);
    // wa.me works on both mobile (opens the app) and desktop (opens WhatsApp Web / desktop app)
    const url = isMobile()
      ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`
      : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encoded}`;

    // Open in a new tab so the current page (and form data) stays intact
    const win = window.open(url, "_blank", "noopener,noreferrer");

    // Fallback: some browsers block popups — navigate current tab as a safety net
    if (!win) {
      window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
    }

    toast.success("Opening WhatsApp to send your enquiry…");
    setTimeout(() => {
      setForm({ name: "", phone: "", email: "", subject: "", course: "", message: "" });
      setLoading(false);
    }, 800);
  };

  return (
    <div>
      <SEO
        title="Contact AM Group of Education — Admissions & Course Enquiry"
        description="Talk to our counsellors about admissions, course enquiries and coaching class details. Reach AM Group of Education via phone or WhatsApp today."
        path="/contact"
        jsonLd={[
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <PageHeader title="Contact Us" subtitle="Talk to a counsellor today. We respond within 24 hours." crumb="Contact" />

      <section className="py-16 bg-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2" data-aos="fade-right">
            <span className="kicker">Get in touch</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl font-bold text-brand-dark">We&apos;d love to hear from you</h2>
            <p className="mt-3 text-brand-mute">Reach out directly — our team responds within a business day.</p>

            <ul className="mt-8 space-y-4">
              <li className="group flex items-start gap-4 rounded-2xl border border-brand-line bg-brand-cream p-5 lift" data-testid="contact-info-phone">
                <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary to-brand-dark text-brand-gold grid place-items-center shadow-md">
                  <i className="fa-solid fa-phone text-lg" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Phone</div>
                  <a href={`tel:${CONTACT.phone}`} className="text-brand-dark font-semibold text-lg hover:text-brand-gold transition-colors">{CONTACT.phone}</a>
                  <div className="text-xs text-brand-mute mt-1">Mon – Sat · 9:00 AM – 8:00 PM</div>
                </div>
              </li>
              <li className="group flex items-start gap-4 rounded-2xl border border-brand-line bg-brand-cream p-5 lift" data-testid="contact-info-email">
                <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary to-brand-dark text-brand-gold grid place-items-center shadow-md">
                  <i className="fa-solid fa-envelope text-lg" />
                </span>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Email</div>
                  <a href={`mailto:${CONTACT.email}`} className="text-brand-dark font-semibold text-lg hover:text-brand-gold transition-colors break-all">{CONTACT.email}</a>
                  <div className="text-xs text-brand-mute mt-1">We reply within 24 hours</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl bg-brand-cream border border-brand-line p-6 sm:p-8" data-aos="fade-left" data-testid="contact-form">
            <h3 className="display-serif text-2xl font-bold text-brand-dark">Send a message</h3>
            <p className="text-sm text-brand-mute mt-1">Fill the form and our counsellor will reach out.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Name *</label>
                <input value={form.name} onChange={update("name")} required data-testid="contact-name" className="mt-1 w-full rounded-lg border border-brand-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold" placeholder="Your full name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Phone *</label>
                <input value={form.phone} onChange={update("phone")} required data-testid="contact-phone" className="mt-1 w-full rounded-lg border border-brand-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold" placeholder="+91…" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Email *</label>
                <input type="email" value={form.email} onChange={update("email")} required data-testid="contact-email" className="mt-1 w-full rounded-lg border border-brand-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold" placeholder="you@example.com" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Course Interested In</label>
                <select value={form.course} onChange={update("course")} data-testid="contact-course" className="mt-1 w-full rounded-lg border border-brand-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold">
                  <option value="">Select a course (optional)</option>
                  <option>Spoken English</option>
                  <option>School (1st - 10th)</option>
                  <option>11th / 12th Science</option>
                  <option>NEET / JEE</option>
                  <option>UPSC / MPSC</option>
                  <option>Government Exams</option>
                  <option>Foreign Languages</option>
                  <option>IT / Programming</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-brand-primary font-semibold">Message *</label>
                <textarea value={form.message} onChange={update("message")} required data-testid="contact-message" rows="4" className="mt-1 w-full rounded-lg border border-brand-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold" placeholder="How can we help you?" />
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-primary mt-6 w-full justify-center">
              {loading ? <><i className="fa-solid fa-spinner fa-spin" /> Opening WhatsApp…</> : <>Send Message <i className="fa-brands fa-whatsapp" /></>}
            </button>
            <p className="mt-3 text-xs text-brand-mute text-center">
              <i className="fa-brands fa-whatsapp text-green-600 mr-1" /> Your enquiry opens directly in WhatsApp — no server, no email delay. Works even if you&apos;re offline briefly.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
